/**
 * Temporary boot-sequence diagnostics for Product Education Platform.
 * Remove when debugging is complete.
 */

import { LEGACY_DASHBOARD_GUIDE_KEY } from "./types";

export function log(message) {
  console.log(`[PEP] ${message}`);
}

function logBlank() {
  console.log("[PEP]");
}

export function logProviderMounted({ userId, screen, authLoading, hasActor }) {
  logBlank();
  log("ProductEducationProvider mounted");
  log(`Screen: ${screen ?? "unknown"}`);
  log(`Auth loading: ${authLoading ? "yes" : "no"}`);
  log(`Actor present: ${hasActor ? "yes" : "no"}`);
  log(`User ID: ${userId ?? "null"}`);
}

export function logUserIdAvailable(userId) {
  logBlank();
  log(`User ID available: ${userId}`);
}

export function logBootGateStarted(context = "evaluate") {
  logBlank();
  log(`EducationBootGate started (${context})`);
}

export function logBootGateSkipped(reason) {
  log(`Skipped because ${reason}`);
}

export function logBootGateDecision({
  legacyOnboardingPending,
  releaseEducation,
  normalStartup,
  presentationType,
  planReason,
}) {
  logBlank();
  log("Boot gate decision:");
  log(`  Legacy onboarding pending: ${legacyOnboardingPending ? "yes" : "no"}`);
  log(`  Release education: ${releaseEducation ? "yes" : "no"}`);
  log(`  Normal startup: ${normalStartup ? "yes" : "no"}`);
  if (presentationType) {
    log(`  Presentation type: ${presentationType}`);
  }
  if (planReason) {
    log(`  Plan reason: ${planReason}`);
  }
}

export function logVersionDetection(detection) {
  logBlank();
  log("VersionDetector");
  log(`  APP_RELEASE_ID: ${detection.currentReleaseId}`);
  log(`  APP_VERSION: ${detection.currentVersion}`);
  log(`  lastSeenReleaseId: ${detection.lastSeenReleaseId ?? "null"}`);
  log(`  isNewRelease: ${detection.isNewRelease ? "yes" : "no"}`);
  log(`  educationPolicy: ${detection.releaseMeta?.educationPolicy ?? "optional (default)"}`);
  log(`  unseen features: ${detection.unseenFeatures?.length ?? 0}`);
  log(`  tour-worthy features: ${detection.tourWorthyFeatures?.length ?? 0}`);
}

export function logExperiencePlannerInput({ detection, state, isGuest, context }) {
  logBlank();
  log("ExperiencePlanner input");
  log(`  context: ${context}`);
  log(`  isGuest: ${isGuest ? "yes" : "no"}`);
  log(`  autoTourEnabled: ${state.settings?.autoTourEnabled ? "yes" : "no"}`);
  log(`  ashleyBriefEnabled: ${state.settings?.ashleyBriefEnabled ? "yes" : "no"}`);
  log(`  autoFirstTimeGuideEnabled: ${state.settings?.autoFirstTimeGuideEnabled !== false ? "yes" : "no"}`);
  log(`  release record status: ${state.releases?.[detection.currentReleaseId]?.status ?? "not_started"}`);
  log(`  opted out of release: ${state.optedOutReleases?.includes(detection.currentReleaseId) ? "yes" : "no"}`);
}

export function logExperiencePlanOutput(plan) {
  logBlank();
  log("ExperiencePlan output");
  log(`  educationPolicy: ${plan.educationPolicy}`);
  log(`  reason: ${plan.reason}`);
  log(`  showBrief: ${plan.showBrief ? "yes" : "no"}`);
  log(`  showRequiredTour: ${plan.showRequiredTour ? "yes" : "no"}`);
  log(`  blockDashboard: ${plan.blockDashboard ? "yes" : "no"}`);
  log(`  showWhatsNewChip: ${plan.showWhatsNewChip ? "yes" : "no"}`);
  log(`  requiredTour: ${plan.requiredTour ? "yes" : "no"}`);
}

export function logReleaseManagerDecision({ presentationAction, presentationType, plan }) {
  logBlank();
  log("ReleaseManager decision");
  log(`  presentation action: ${presentationAction}`);
  log(`  resolved presentation: ${presentationType}`);

  if (plan.educationPolicy === "mandatory" && (plan.showBrief || plan.showRequiredTour)) {
    log("Mandatory release detected");
  }

  if (presentationType === "brief") {
    log("Launching Ashley Release Brief");
  } else if (presentationType === "required_tour") {
    log("Launching required tour");
  } else if (presentationType === "whats_new_chip") {
    log("Showing What's New chip only");
  } else if (presentationType === "none") {
    log("No education UI will be shown");
    log(`Skipped because plan reason is: ${plan.reason}`);
  }
}

export function logAshleyBriefMounted({ title, briefUiMode }) {
  logBlank();
  log("AshleyReleaseBriefPresenter mounted");
  log(`  Release title: ${title ?? "unknown"}`);
  log(`  Brief UI mode: ${briefUiMode ?? "unknown"}`);
}

export function logEducationSkipped(reason) {
  logBlank();
  log(`Skipped because ${reason}`);
}

export function describeShouldEvaluateEducation({
  screen,
  authLoading,
  hasUser,
  userId,
  autoEvaluatedRef,
}) {
  if (authLoading) {
    return "auth is still loading";
  }
  if (!hasUser) {
    return "no signed-in user is available yet";
  }
  if (!userId) {
    return "authenticated user ID is not resolved yet";
  }
  if (
    screen === "onboarding" ||
    screen === "email_confirmation" ||
    screen === "update_password" ||
    screen === "payroll_print_isolation"
  ) {
    return `current screen is an auth flow (${screen})`;
  }
  if (screen !== "kpi_dashboard") {
    return `current screen is not kpi_dashboard (screen=${screen ?? "unknown"})`;
  }
  if (autoEvaluatedRef?.current) {
    return "education was already evaluated this session";
  }
  return null;
}

export function describeLegacyOnboardingPending(settings) {
  if (settings?.autoFirstTimeGuideEnabled === false) {
    return false;
  }
  if (typeof window === "undefined") {
    return false;
  }
  return !window.localStorage.getItem(LEGACY_DASHBOARD_GUIDE_KEY);
}
